// src/pages/LibraryPage.js
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const LibraryPage = ({ language }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [libraryCategories, setLibraryCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const navigate = useNavigate();

  const featuredBooks = [
    {
      title: {
        en: "Modern Training Techniques",
        ar: "تقنيات التدريب الحديثة",
      },
      author: { en: "Dr. Mohammed Ali", ar: "د. محمد علي" },
      year: "2023",
    },
    {
      title: {
        en: "Dual Training Methodology",
        ar: "منهجية التدريب المزدوج",
      },
      author: { en: "Prof. Sarah Johnson", ar: "أ. سارة جونسون" },
      year: "2022",
    },
    {
      title: {
        en: "Employee Development Strategies",
        ar: "استراتيجيات تطوير الموظفين",
      },
      author: { en: "Dr. Ahmed Hassan", ar: "د. أحمد حسن" },
      year: "2023",
    },
    {
      title: {
        en: "Advanced Leadership Skills",
        ar: "مهارات القيادة المتقدمة",
      },
      author: { en: "Dr. Fatima Al-Mansoori", ar: "د. فاطمة المنصوري" },
      year: "2023",
    },
    {
      title: {
        en: "Digital Transformation Guide",
        ar: "دليل التحول الرقمي",
      },
      author: { en: "Tech Experts Team", ar: "فريق الخبراء التقنيين" },
      year: "2024",
    },
    {
      title: {
        en: "Sustainable Development Goals",
        ar: "أهداف التنمية المستدامة",
      },
      author: {
        en: "UN Development Program",
        ar: "برنامج الأمم المتحدة الإنمائي",
      },
      year: "2023",
    },
  ];

  useEffect(() => {
    axios
      .get(
        process.env.REACT_APP_BASE_URL +
          "library-category/get-all-library-category"
      )
      .then((res) => {
        setLibraryCategories(res.data.data);
        setFilteredCategories(res.data.data);
      });
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = libraryCategories.filter(
        (category) =>
          category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          category.title_ar.includes(searchQuery) ||
          category.description
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          category.description_ar?.includes(searchQuery)
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories(libraryCategories);
    }
  }, [searchQuery, libraryCategories]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleCategoryClick = (category) => {
    // Navigate to category details page or filter library page
    navigate(`/books?category=${category.id}`);
  };

  const handleDownload = (book) => {
    console.log("Downloading:", book.title[language]);
    // Implement download logic here
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div
        className="container mx-auto px-4"
        style={{
          marginTop: "10rem",
        }}
      >
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={handleBack}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg mb-6"
          >
            {language === "ar" ? "العودة" : "Back"}
          </button>

          <h1 className="text-4xl md:text-5xl font-bold text-green-900 mb-4">
            {language === "ar" ? "المكتبة الشاملة" : "Comprehensive Library"}
          </h1>
          <div className="w-32 h-1 bg-green-600 mx-auto"></div>
          <p className="text-gray-600 max-w-2xl mx-auto mt-4">
            {language === "ar"
              ? "استكشف جميع فئات مكتبتنا الغنية بالمصادر المعرفية والمراجع التدريبية المتخصصة"
              : "Explore all categories of our rich library filled with knowledge sources and specialized training references"}
          </p>
        </div>

        {/* Enhanced Search Bar */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder={
                language === "ar"
                  ? "ابحث في جميع فئات المكتبة..."
                  : "Search across all library categories..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-4 border-2 border-green-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg shadow-lg"
            />
            <button className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-green-600 text-white p-3 rounded-lg hover:bg-green-700 transition-colors">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
          </div>
          {searchQuery && (
            <p className="text-green-700 text-center mt-2">
              {language === "ar" ? "نتائج البحث عن:" : "Search results for:"} "
              {searchQuery}" - {filteredCategories.length}{" "}
              {language === "ar" ? "فئة" : "categories"}
            </p>
          )}
        </div>

        {/* All Library Categories */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-green-900 mb-8 text-center">
            {language === "ar" ? "جميع فئات المكتبة" : "All Library Categories"}
          </h3>

          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-xl">
                {language === "ar"
                  ? "لا توجد فئات تطابق بحثك"
                  : "No categories match your search"}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCategories.map((category, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 border-l-4 border-green-600 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 shadow-lg"
                >
                  <div className="flex items-center mb-4">
                    <span className="text-4xl mr-4 rtl:mr-0 rtl:ml-4">
                      {category.icon || "📚"}
                    </span>
                    <div>
                      <h4 className="font-bold text-green-900 text-xl">
                        {language === "ar" ? category.title_ar : category.title}
                      </h4>
                      <p className="text-green-700 font-semibold">
                        {category.bookCount}{" "}
                        {language === "ar" ? "عنصر" : "items"}
                      </p>
                    </div>
                  </div>

                  {category.description && (
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {language === "ar"
                        ? category.description_ar
                        : category.description}
                    </p>
                  )}

                  <button
                    onClick={() => handleCategoryClick(category)}
                    className="w-full mt-2 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors text-lg"
                  >
                    {language === "ar" ? "استعرض المحتوى" : "Browse Content"} →
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Enhanced Featured Books Section */}
        <div className="bg-gradient-to-r from-green-800 to-green-900 rounded-2xl p-8 text-white mb-12">
          <h3 className="text-3xl font-bold mb-8 text-center">
            {language === "ar" ? "الإصدارات المميزة" : "Featured Publications"}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredBooks.map((book, index) => (
              <div
                key={index}
                className="bg-green-700 bg-opacity-50 backdrop-blur-sm rounded-xl p-6 hover:bg-opacity-70 transition-all duration-300 transform hover:-translate-y-1 border border-green-600"
              >
                <h4 className="font-bold text-xl mb-3">
                  {book.title[language]}
                </h4>
                <p className="text-green-200 mb-2">
                  {language === "ar" ? "المؤلف:" : "Author:"}{" "}
                  <span className="font-semibold">{book.author[language]}</span>
                </p>
                <p className="text-green-200 mb-4">
                  {language === "ar" ? "سنة الإصدار:" : "Publication Year:"}{" "}
                  <span className="font-semibold">{book.year}</span>
                </p>
                <button
                  onClick={() => handleDownload(book)}
                  className="bg-white text-green-800 px-4 py-3 rounded-lg font-semibold hover:bg-green-100 transition-colors w-full text-lg"
                >
                  {language === "ar" ? "تحميل النسخة" : "Download"} ↓
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced Library Stats */}
        <div className="bg-green-50 rounded-2xl p-8">
          <h3 className="text-3xl font-bold text-green-900 mb-8 text-center">
            {language === "ar" ? "إحصائيات المكتبة" : "Library Statistics"}
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 max-w-4xl mx-auto">
            {[
              { number: "60+", label: { en: "Books", ar: "كتاب" } },
              {
                number: "2+",
                label: { en: "Research Papers", ar: "بحث علمي" },
              },
              {
                number: "3+",
                label: { en: "Training Manuals", ar: "دليل تدريبي" },
              },
              // {
              //   number: "200+",
              //   label: { en: "Video Courses", ar: "دورة فيديو" },
              // },
              // {
              //   number: "150+",
              //   label: { en: "Audio Books", ar: "كتاب مسموع" },
              // },
              // {
              //   number: "50+",
              //   label: { en: "Journals", ar: "دورية" },
              // },
              {
                number: "24/7",
                label: { en: "Digital Access", ar: "وصول رقمي" },
              },
              {
                number: "100%",
                label: { en: "Free Access", ar: "وصول مجاني" },
              },
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center bg-white rounded-lg p-4 shadow-md"
              >
                <div className="text-2xl lg:text-3xl font-bold text-green-800 mb-2">
                  {stat.number}
                </div>
                <div className="text-green-700 font-semibold">
                  {language === "ar" ? stat.label.ar : stat.label.en}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibraryPage;
